import { createStore, type StoreApi } from 'zustand';

import { isProductionRuntime } from '@/lib/runtime-flags';
import { scheduleMicrotask } from '@/lib/utils/scheduleMicrotask';

import type { ProfileResponse } from '@/lib/validation/api';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';

export const USER_PREVIEW_CACHE_TTL_MS = 5 * 60 * 1000;

export type UserPreviewCacheDetailLevel = 'partial' | 'full';

// eslint-disable-next-line no-unused-vars
export interface UserPreviewCacheEntry {
  aliases: string[];
  detail: UserPreviewCacheDetailLevel;
  error: boolean;
  fetchedAt: number;
  preview?: UserPreviewVM;
  profile?: ProfileResponse;
  usernameKey?: string;
}

interface UserPreviewCacheState {
  entries: Map<string, UserPreviewCacheEntry>;
  getEntry: (primaryKey: string) => UserPreviewCacheEntry | undefined;
  markAlias: (primaryKey: string, alias: string) => void;
  remove: (primaryKey: string) => void;
  reset: () => void;
  resolvePrimaryKey: (lookup: {
    primaryKey?: string | null;
    usernameKey?: string | null;
    cacheKey?: string | null;
  }) => string | undefined;
  upsert: (primaryKey: string, entry: Omit<UserPreviewCacheEntry, 'aliases'> & { aliases?: string[] }) => void;
}

export type UserPreviewStoreApi = StoreApi<UserPreviewCacheState>;

const createInitialState = () => ({
  entries: new Map<string, UserPreviewCacheEntry>(),
});

const getNow = () => Date.now();

const isExpired = (entry: UserPreviewCacheEntry) => getNow() - entry.fetchedAt > USER_PREVIEW_CACHE_TTL_MS;

type PendingUpsertEntry = Omit<UserPreviewCacheEntry, 'aliases'> & { aliases?: string[] };

const collectAliases = (primaryKey: string, previous: UserPreviewCacheEntry | undefined, incoming?: string[]) => {
  const aliases = new Set(previous?.aliases);
  aliases.add(primaryKey);
  if (Array.isArray(incoming)) {
    for (const alias of incoming) {
      if (alias) {
        aliases.add(alias);
      }
    }
  }
  return aliases;
};

const toOptional = <T>(value: T | null | undefined): T | undefined => (value === null ? undefined : value);

const arraysEqual = (left: readonly string[], right: readonly string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areEntriesEqual = (left: UserPreviewCacheEntry | undefined, right: UserPreviewCacheEntry) =>
  Boolean(
    left &&
    left.error === right.error &&
    left.fetchedAt === right.fetchedAt &&
    left.detail === right.detail &&
    arraysEqual(left.aliases, right.aliases) &&
    left.preview === right.preview &&
    left.profile === right.profile &&
    left.usernameKey === right.usernameKey
  );

const buildCacheSnapshot = (
  primaryKey: string,
  upsert: PendingUpsertEntry,
  previous: UserPreviewCacheEntry | undefined,
  now: () => number
): UserPreviewCacheEntry => {
  const aliases = collectAliases(primaryKey, previous, upsert.aliases);
  const nextPreview = Object.hasOwn(upsert, 'preview') ? toOptional(upsert.preview) : previous?.preview;
  const nextProfile = Object.hasOwn(upsert, 'profile') ? toOptional(upsert.profile) : previous?.profile;
  const nextUsernameKey = Object.hasOwn(upsert, 'usernameKey') ? toOptional(upsert.usernameKey) : previous?.usernameKey;
  const detail = upsert.detail ?? (nextProfile ? 'full' : undefined) ?? previous?.detail ?? 'partial';

  return {
    error: Boolean(upsert.error),
    fetchedAt: typeof upsert.fetchedAt === 'number' ? upsert.fetchedAt : now(),
    detail,
    aliases: [...aliases],
    ...(nextPreview ? { preview: nextPreview } : {}),
    ...(nextProfile ? { profile: nextProfile } : {}),
    ...(nextUsernameKey ? { usernameKey: nextUsernameKey } : {}),
  };
};

const mergeUpsertBatch = (
  entries: Map<string, UserPreviewCacheEntry>,
  batch: readonly [string, PendingUpsertEntry][],
  now: () => number
) => {
  let workingEntries: Map<string, UserPreviewCacheEntry> | undefined;

  for (const [primaryKey, upsert] of batch) {
    const sourceEntries = workingEntries ?? entries;
    const previous = sourceEntries.get(primaryKey);
    const snapshot = buildCacheSnapshot(primaryKey, upsert, previous, now);

    if (areEntriesEqual(previous, snapshot)) {
      continue;
    }

    if (!workingEntries) {
      workingEntries = new Map(entries);
    }

    workingEntries.set(primaryKey, snapshot);
  }

  return workingEntries ?? entries;
};

// Zustand keeps the in-memory view for React consumers; persistence lives in
// TanStack DB (`src/lib/cache/user-preview-cache.ts`). The cache mutators write
// through to this store so components can keep their simple selector-based reads.
export const createUserPreviewStore = (): UserPreviewStoreApi =>
  createStore<UserPreviewCacheState>((set, get) => {
    const pendingRemovals = new Set<string>();
    // React can call into the cache while components are rendering; pushing
    // updates directly into Zustand during that phase would trip the
    // "Cannot update a component while rendering a different component" guard.
    // We buffer the writes and flush them via microtask once the current render
    // frame has finished so subscribers see a consistent post-commit snapshot.
    const pendingUpserts = new Map<string, PendingUpsertEntry>();
    let upsertFlushScheduled = false;

    const flushPendingUpserts = () => {
      if (pendingUpserts.size === 0) {
        return;
      }
      const batch = [...pendingUpserts.entries()];
      pendingUpserts.clear();
      set((state) => {
        const nextEntries = mergeUpsertBatch(state.entries, batch, getNow);
        if (nextEntries === state.entries) {
          return state;
        }
        return { entries: nextEntries };
      });
    };

    const scheduleUpsertFlush = () => {
      if (!upsertFlushScheduled) {
        upsertFlushScheduled = true;
        // queueMicrotask keeps latency low while still letting React finish the
        // current render pass before subscribers are notified.
        scheduleMicrotask(() => {
          upsertFlushScheduled = false;
          flushPendingUpserts();
        });
      }
    };

    const enqueueRemoval = (primaryKey: string) => {
      if (pendingRemovals.has(primaryKey)) {
        return;
      }
      pendingRemovals.add(primaryKey);
      scheduleMicrotask(() => {
        pendingRemovals.delete(primaryKey);
        set((state) => {
          if (!state.entries.has(primaryKey)) {
            return state;
          }
          const next = new Map(state.entries);
          next.delete(primaryKey);
          return { entries: next };
        });
      });
    };

    return {
      ...createInitialState(),
      upsert: (primaryKey, entry) => {
        const { aliases, ...rest } = entry;
        pendingUpserts.set(primaryKey, {
          ...rest,
          ...(Array.isArray(aliases) ? { aliases: [...aliases] } : {}),
        });
        scheduleUpsertFlush();
      },
      getEntry: (primaryKey) => {
        const entry = get().entries.get(primaryKey);
        if (!entry) {
          return;
        }
        if (isExpired(entry)) {
          enqueueRemoval(primaryKey); // Previously we called remove() immediately here, which triggered React's render-phase setState warning.
          return;
        }
        return entry;
      },
      remove: (primaryKey) => {
        set((state) => {
          if (!state.entries.has(primaryKey)) {
            return state;
          }
          const next = new Map(state.entries);
          next.delete(primaryKey);
          return { entries: next };
        });
      },
      resolvePrimaryKey: ({ primaryKey, usernameKey, cacheKey }) => {
        const entries = get().entries;
        const candidates = new Set<string>();
        if (primaryKey) {
          candidates.add(primaryKey);
        }
        if (cacheKey) {
          candidates.add(cacheKey);
        }
        if (usernameKey) {
          for (const [key, entry] of entries.entries()) {
            if (entry.usernameKey === usernameKey) {
              return key;
            }
          }
        }
        for (const candidate of candidates) {
          if (entries.has(candidate)) {
            return candidate;
          }
          for (const [key, entry] of entries.entries()) {
            if (entry.aliases.includes(candidate)) {
              return key;
            }
          }
        }
        return;
      },
      markAlias: (primaryKey, alias) => {
        if (!alias) {
          return;
        }
        set((state) => {
          const entry = state.entries.get(primaryKey);
          if (!entry || entry.aliases.includes(alias)) {
            return state;
          }
          const next = new Map(state.entries);
          next.set(primaryKey, {
            ...entry,
            aliases: [...entry.aliases, alias],
          });
          return { entries: next };
        });
      },
      reset: () => {
        pendingUpserts.clear();
        set(createInitialState());
      },
    };
  });

export type UserPreviewStore = ReturnType<typeof createUserPreviewStore>;

export const userPreviewStore = createUserPreviewStore();

declare global {
  interface Window {
    __SWEETISTICS_USER_PREVIEW_STORE__?: UserPreviewStore;
  }
}

if (!isProductionRuntime()) {
  const windowObject = globalThis.window;
  if (windowObject) {
    windowObject.__SWEETISTICS_USER_PREVIEW_STORE__ = userPreviewStore;
  }
}
